import React, { useState } from 'react';
import {
  Accordion as ChakraAccordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Flex,
  Container,
  Button,
} from '@chakra-ui/react';
import { RichText } from '../RichText';
import { useIsTabbing } from '../../hooks/useIsTabbing';
import { Divider } from '../Divider';

function Accordion({ data }) {
  const isTabbing = useIsTabbing();
  const { items, heading, source, tableNumber, narrowColumn } = data || {};
  // State to track the expanded state of the accordion items
  const [expandedIndexes, setExpandedIndexes] = useState([]); // Initially, no items are expanded.

  // Function to toggle the expanded state of all accordion items
  const toggleExpandAll = () => {
    if (expandedIndexes.length === items.length) {
      // If all items are expanded, collapse all.
      setExpandedIndexes([]);
    } else {
      // If not all items are expanded, expand all.
      setExpandedIndexes(items.map((_, index) => index));
    }
  };

  // Function to handle expanding/collapsing an accordion item
  const handleAccordionItemToggle = (index) => {
    if (expandedIndexes.includes(index)) {
      setExpandedIndexes(expandedIndexes.filter((i) => i !== index)); // Collapse the item
    } else {
      setExpandedIndexes([...expandedIndexes, index]); // Expand the item
    }
  };

  return (
    <Container as='section' maxWidth={narrowColumn ? 'container.md' : 'container.xl'}>
      <Divider />
      <Flex
        as='header'
        mb={20}
        gap={20}
        direction={{ base: 'column', xl: 'row' }}
        justifyContent='space-between'
      >
        <Box>
          {tableNumber && (
            <Text fontSize='xs' textTransform='uppercase' fontWeight={500} letterSpacing={1}>
              Table {tableNumber}
            </Text>
          )}
          <Text textStyle='h3' as='h2'>
            {heading}
          </Text>
        </Box>
        <Button onClick={toggleExpandAll} mb={4}>
          {expandedIndexes.length === items.length ? 'Collapse All' : 'Expand All'}
        </Button>
      </Flex>
      <ChakraAccordion index={expandedIndexes} allowMultiple>
        {items.map(({ _key, heading, content }, index) => (
          <AccordionItem
            key={_key}
            _focusWithin={{
              boxShadow: isTabbing ? 'var(--chakra-shadows-outline)' : null,
            }}
          >
            <AccordionButton
              _expanded={{
                boxShadow: 'inset 0px 3px 0px 0 navy',
                bg: 'lightBlue',
              }}
              py={20}
              onClick={() => handleAccordionItemToggle(index)}
            >
              <Box flex='1' textAlign='left'>
                <Text fontSize='md' fontWeight={600}>
                  {heading}
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel py={20}>
              <Flex gap={20} flexDirection={{ base: 'column', md: 'row' }}>
                {content.map(({ _key, content, heading }) => (
                  <Box key={_key} flex='1' textAlign='left'>
                    <Text
                      fontSize='xs'
                      letterSpacing='0.5px'
                      fontWeight={600}
                      textTransform='uppercase'
                    >
                      {heading}
                    </Text>
                    <RichText value={content} />
                  </Box>
                ))}
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </ChakraAccordion>
      <Box as='footer' mt={20}>
        {source && <RichText flex='1' value={source} fontSize='sm' mt={5} lineHeight={1.1} />}
      </Box>
    </Container>
  );
}

export default Accordion;
